import { isHTTPError } from "ky";
import { defineStore } from "pinia";
import {
  type GetOrGenerateApiKeyResponse,
  IntegrationStatusEnum,
  type IntegrationStatusResponse,
} from "shared-core/types/integrations";
import type { MetaBusinessAccount } from "shared-core/types/meta";
import { SystemId } from "shared-core/types/system.ts";
import awsLogo from "shared-ui/assets/system-logos/aws.svg";
import azureLogo from "shared-ui/assets/system-logos/azure.svg";
import gcpLogo from "shared-ui/assets/system-logos/gcp.svg";
import googleWorkspaceLogo from "shared-ui/assets/system-logos/google_workspace.svg";
import metaBusinessSuiteLogo from "shared-ui/assets/system-logos/meta_business_suite.svg";
import microsoft365Logo from "shared-ui/assets/system-logos/microsoft_365.svg";
import wordpressLogo from "shared-ui/assets/system-logos/wordpress.svg";
import type {
  IntegrationStatusMap,
  IntegrationStoreActions,
  IntegrationStoreState,
} from "shared-ui/types/integration-store.ts";
import { useNavigationService } from "../services/navigation.ts";
import { useApi } from "../utils/api.ts";

export const useIntegrationStore = defineStore("integration", {
  state: (): IntegrationStoreState => ({
    metaBusinessAccounts: [],
    // Static integration data as a map for easier lookup
    integrations: {
      [SystemId.GOOGLE_WORKSPACE]: {
        id: SystemId.GOOGLE_WORKSPACE,
        name: "Google Workspace",
        logo: googleWorkspaceLogo,
        description:
          "Connect your Google Workspace to get insight into identity security.",
        securityChecks: [
          {
            name: "MFA Status Check",
            description: "Verifies MFA is enabled for all users",
            enabled: true,
          },
          {
            name: "MFA Available and Enforced",
            description: "Verifies MFA is available and enforced",
            enabled: true,
          },
          // {
          //   name: "Password Policy Check",
          //   description: "Verify password policies meet requirements",
          //   enabled: true,
          // },
          // {
          //   name: "Admin Privileges Audit",
          //   description: "Audit admin privilege assignments",
          //   enabled: true,
          // },
          // {
          //   name: "Suspicious Login Detection",
          //   description: "Detect suspicious login attempts",
          //   enabled: false,
          // },
        ],
        status: IntegrationStatusEnum.AVAILABLE,
        lastScan: null,
        connectedSince: null,
      },
      [SystemId.META_BUSINESS_SUITE]: {
        id: SystemId.META_BUSINESS_SUITE,
        name: "Meta",
        logo: metaBusinessSuiteLogo,
        description:
          "Connect your Facebook and Instagram account to get insight into configuration settings and security best practices.",
        securityChecks: [
          {
            name: "MFA Enforcement",
            description: "Verify MFA is enforced for all business users",
            enabled: true,
          },
          // {
          //   name: "Admin Access Review",
          //   description: "Review admin access permissions",
          //   enabled: true,
          // },
          // {
          //   name: "Page Access Control",
          //   description: "Verify page access controls are properly set up",
          //   enabled: false,
          // },
          // {
          //   name: "Ad Account Security",
          //   description: "Check ad account security settings",
          //   enabled: true,
          // },
        ],
        status: IntegrationStatusEnum.AVAILABLE,
        lastScan: null,
        connectedSince: null,
      },
      [SystemId.MICROSOFT_365]: {
        id: SystemId.MICROSOFT_365,
        name: "Microsoft 365",
        logo: microsoft365Logo,
        description:
          "Connect your M365 account to get insight into configuration settings, identity security, and security best practices.",
        status: IntegrationStatusEnum.AVAILABLE,
        lastScan: null,
        securityChecks: [],
        connectedSince: null,
      },
      [SystemId.WORDPRESS]: {
        id: SystemId.WORDPRESS,
        name: "WordPress",
        logo: wordpressLogo,
        description:
          "Install Controla's Wordpress plugin to get insight into configuration settings and security best practices.",
        status: IntegrationStatusEnum.AVAILABLE,
        lastScan: null,
        securityChecks: [],
        connectedSince: null,
      },
      [SystemId.AMAZON_WEB_SERVICES]: {
        id: SystemId.AMAZON_WEB_SERVICES,
        name: "Amazon (AWS)",
        logo: awsLogo,
        description:
          "Connect your Amazon AWS account to get insight into identity security, and data security settings.",
        status: IntegrationStatusEnum.COMING_SOON,
        lastScan: null,
        securityChecks: [],
        connectedSince: null,
      },
      [SystemId.AZURE]: {
        id: SystemId.AZURE,
        name: "Azure",
        logo: azureLogo,
        description:
          "Connect your Azure account to get insight into cloud security posture and threat protection.",
        status: IntegrationStatusEnum.COMING_SOON,
        lastScan: null,
        securityChecks: [],
        connectedSince: null,
      },
      [SystemId.GOOGLE_CLOUD_PLATFORM]: {
        id: SystemId.GOOGLE_CLOUD_PLATFORM,
        name: "Google Cloud Platform (GCP)",
        logo: gcpLogo,
        description:
          "Connect your GCP account to get insight into identity security, and data security settings.",
        status: IntegrationStatusEnum.COMING_SOON,
        lastScan: null,
        securityChecks: [],
        connectedSince: null,
      },
    },
    isLoading: false,
  }),

  actions: {
    async fetchIntegrationStatuses(): Promise<void> {
      this.isLoading = true;

      try {
        // Fetch all integration statuses from the unified endpoint
        const response =
          await useApi().get<
            Partial<Record<SystemId, IntegrationStatusResponse>>
          >("integrations");

        // Update integration statuses for all integrations in the response
        Object.entries(await response.data).forEach(([key, value]) => {
          // Check if we have this integration in our store
          if (this.integrations[key]) {
            this.integrations[key] = { ...this.integrations[key], ...value };
          }
        });
      } catch (error) {
        console.error("Failed to fetch integration statuses:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async disconnectIntegration(integrationId: string): Promise<boolean> {
      try {
        // Call the API to disconnect the integration
        await useApi().delete(`integrations/${integrationId}`);

        // Update the integration status directly using the ID
        if (this.integrations[integrationId]) {
          this.integrations[integrationId].status =
            IntegrationStatusEnum.AVAILABLE;
        }

        return true;
      } catch (error) {
        const integrationName =
          this.integrations[integrationId]?.name || integrationId;
        console.error(
          `Failed to disconnect ${integrationName} integration:`,
          error,
        );
        return false;
      }
    },

    /**
     * Enable MFA for Facebook Business Manager
     * @returns {Promise<Object>} The response data
     */
    async enableFacebookMfa(): Promise<object> {
      try {
        const response = await useApi().post("facebook/enable-mfa");

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        return response.data as object;
      } catch (error) {
        console.error("Failed to enable Facebook MFA:", error);

        if (isHTTPError<{ message?: string; error?: string }>(error)) {
          const response = error.data;
          if (typeof response === "object" && response !== null) {
            throw new Error(
              response.message ||
                response.error ||
                "Failed to enable Facebook MFA",
            );
          }
        }
        throw new Error("Failed to enable Facebook MFA");
      }
    },

    connectToFacebook(): void {
      useNavigationService().navigateTo("/api/facebook/auth");
    },

    connectToGoogleWorkspace(): void {
      useNavigationService().navigateTo("/api/google/integrate");
    },

    connectToMicrosoft365(): void {
      useNavigationService().navigateTo("/api/microsoft/integration");
    },

    async getOrGenerateApiKey(integrationId: string): Promise<string> {
      const response = await useApi().get<GetOrGenerateApiKeyResponse>(
        `integrations/get-or-generate-api-key?integrationId=${integrationId}`,
      );
      return (await response.data).apiKey;
    },

    async generateApiKey(integrationId: string): Promise<string> {
      const response = await useApi().get<GetOrGenerateApiKeyResponse>(
        `integrations/generate-api-key?integrationId=${integrationId}`,
      );
      return (await response.data).apiKey;
    },

    downloadWordPressPlugin(): void {
      useNavigationService().navigateTo(
        "https://0r74q.upcloudobjects.com/wordpress-plugin-bucket/controla-wordpress-plugin.zip",
      );
    },

    /**
     * Update the status of an integration
     * @param {string} integrationId - The integration ID
     * @param {IntegrationStatusEnum} status - The new status
     */
    updateIntegrationStatus(
      integrationId: string,
      status: IntegrationStatusEnum,
    ): void {
      // Update the integration status directly by ID
      if (this.integrations[integrationId]) {
        this.integrations[integrationId].status = status;
      }
    },

    getIntegrations(): IntegrationStatusMap {
      const result: IntegrationStatusMap = {};

      // Return a copy of the integrations map
      for (const [id, integration] of Object.entries(this.integrations)) {
        result[id] = { ...integration };
      }

      return result;
    },

    async fetchMetaBusinessAccounts(): Promise<void> {
      this.isLoading = true;
      try {
        const { data } = await useApi().get<MetaBusinessAccount[]>(
          `integrations/${SystemId.META_BUSINESS_SUITE}/accounts`,
        );
        this.metaBusinessAccounts = await data;
      } catch (error) {
        console.error("Failed to fetch meta business accounts:", error);
      } finally {
        this.isLoading = false;
      }
    },
  } satisfies IntegrationStoreActions &
    ThisType<IntegrationStoreState & IntegrationStoreActions>,
});
